import _ from "lodash";
import axios from "axios";
import store from "@/store";
import moment from "moment";

import { getProfile, getInteractions } from "@/api/traits.api";
import { getUsers, getDepartments } from "@/api/stakeholders.api";

import { extractValueFromProfile, calcEvolution } from "@/utils/helpers";

const state = {
  interactions: [],
  cybersecurity: {},
  wellbeing: {},
  risk: false,
  score: false,
  progress: 0,
  users: [],
  departments: [],
  dashboardStatus: "",
  data: false
};

const getters = {
  interactions: state => state.interactions,
  cybersecurity: state => state.cybersecurity,
  cybersecurityEvolution: state => {
    return calcEvolution(
      "DIMENSION_GROUP_CIBERSECURITY",
      "DIMENSION_ELEMENT_CIBER_RESULTS",
      "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL",
      state.interactions,
      state.data
    );
  },
  wellbeing: state => state.wellbeing || -1,
  wellbeingEvolution: state => {
    return calcEvolution(
      "DIMENSION_GROUP_WORKING",
      "DIMENSION_ELEMENT_CLIMATE_LEVEL",
      "SURVEY_KYMATIO_CLIMATE_GLOBAL",
      state.interactions,
      state.data
    );
  },
  risk: state => (state.risk ? 12 - 2 * state.risk : -1),
  riskEvolution: state => {
    return calcEvolution(
      "DIMENSION_GROUP_RISK",
      "DIMENSION_ELEMENT_AGGREGATED_RISK",
      "SURVEY_KYMATIO_GRI_GLOBAL",
      state.interactions,
      state.data
    );
  },
  score: state => (state.score ? state.score : -1),
  scoreEvolution: state => {
    return calcEvolution(
      "DIMENSION_GROUP_SCORING",
      !store.getters["Auth/hasModule"](["risk"])
        ? "DIMENSION_ELEMENT_KYMATIO_SCORE"
        : "DIMENSION_ELEMENT_KYMATIO_SCORE_OLD",
      "SURVEY_KYMATIO_SCORE_GLOBAL",
      state.interactions,
      state.data
    );
  },
  progress: state => state.progress,
  progressEvolution: state => {
    if (!store.getters["Auth/hasModule"](["risk"])) {
      return calcEvolution(
        "DIMENSION_GROUP_SCORING",
        "DIMENSION_ELEMENT_KYMATIO_GLOBAL_NOTE",
        "SURVEY_KYMATIO_SCORE_GLOBAL",
        state.interactions,
        state.data
      );
    } else {
      return {
        series: [
          {
            date: moment()
              .subtract(1, "week")
              .format("YYYY-MM-DD"),
            value: state.progress
          },
          {
            date: moment().format("YYYY-MM-DD"),
            value: state.progress
          }
        ],
        discrete: [
          {
            seriesIndex: 0,
            dataPointIndex: 0,
            size: 0
          },
          {
            seriesIndex: 0,
            dataPointIndex: 2,
            size: 0
          },
          {
            seriesIndex: 2,
            dataPointIndex: 0,
            size: 0
          },
          {
            seriesIndex: 2,
            dataPointIndex: 2,
            size: 0
          }
        ]
      };
    }
  },
  users: state => state.users,
  usersnum: state => state.users.length,
  departments: state => state.departments,
  modules: state => {
    var result = [
      {
        id: "risk",
        image: "custom-14.svg",
        score: state.risk || -1,
        class: "success",
        service: "risk"
      },
      {
        id: "cybersecurity",
        image: "custom-14.svg",
        score: state.cybersecurity.average || -1,
        class: "success",
        service: "awareness"
      },
      {
        id: "wellbeing",
        image: "custom-14.svg",
        score: state.wellbeing.average || -1,
        class: "success",
        service: "wellbeing"
      },
      {
        id: "progress",
        image: "custom-14.svg",
        score: state.progress || -1,
        class: "success",
        service: "risk",
        base: 10
      }
    ];
    return _.filter(result, item => {
      return (
        item.service === undefined ||
        store.getters["Auth/hasModule"]([item.service])
      );
    });
  },
  isDashboardLoaded: state => state.dashboardStatus !== ""
};

const actions = {
  loadDashboard({ commit, rootGetters }) {
    var stakeholderCompanyId = localStorage.getItem("companyId");
    var profile = {
      surveyTypes: [
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_INSIDER_RED"),
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_INSIDER_GREEN"),
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_INSIDER_BLUE"),
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_ARCHETYPE"),
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_IMPACT"),
        rootGetters["Constants/getConstant"](
          "SURVEY_KYMATIO_INSIDER_BLUE_REFRESH"
        ),
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_ARCHETYPE_REFRESH"),
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_IMPACT_REFRESH"),
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_CLIMATE_GLOBAL"),
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_SCORE_GLOBAL"),
        rootGetters["Constants/getConstant"]("SURVEY_KYMATIO_GRI_GLOBAL"),
        rootGetters["Constants/getConstant"](
          "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL"
        )
      ],
      groupDimensions: [
        rootGetters["Constants/getConstant"]("DIMENSION_GROUP_WORKING"),
        rootGetters["Constants/getConstant"]("DIMENSION_GROUP_CIBERSECURITY"),
        rootGetters["Constants/getConstant"]("DIMENSION_GROUP_SCORING"),
        rootGetters["Constants/getConstant"]("DIMENSION_GROUP_RISK"),
        rootGetters["Constants/getConstant"]("DIMENSION_GROUP_EXTRAS")
      ],
      dimensions: [
        rootGetters["Constants/getConstant"](
          "DIMENSION_ELEMENT_PERCENTAGE_INTERVIEW"
        ),
        rootGetters["Constants/getConstant"]("DIMENSION_ELEMENT_CLIMATE_LEVEL"),
        rootGetters["Constants/getConstant"]("DIMENSION_ELEMENT_CIBER_RESULTS"),
        rootGetters["Constants/getConstant"](
          "DIMENSION_ELEMENT_DATAPROTECTION"
        ),
        rootGetters["Constants/getConstant"]("DIMENSION_ELEMENT_MALWARE"),
        rootGetters["Constants/getConstant"](
          "DIMENSION_ELEMENT_COMMUNICATIONS"
        ),
        rootGetters["Constants/getConstant"](
          "DIMENSION_ELEMENT_PASSWORDMANAGEMENT"
        ),
        rootGetters["Constants/getConstant"]("DIMENSION_ELEMENT_WORKPLACE"),
        rootGetters["Constants/getConstant"](
          "DIMENSION_ELEMENT_SOCIALENGINEERING"
        ),
        rootGetters["Constants/getConstant"]("DIMENSION_ELEMENT_COMPLIANCE"),
        rootGetters["Constants/getConstant"]("DIMENSION_ELEMENT_KYMATIO_SCORE"),
        rootGetters["Constants/getConstant"](
          "DIMENSION_ELEMENT_KYMATIO_SCORE_OLD"
        ),
        rootGetters["Constants/getConstant"](
          "DIMENSION_ELEMENT_KYMATIO_GLOBAL_NOTE"
        ),
        rootGetters["Constants/getConstant"](
          "DIMENSION_ELEMENT_AGGREGATED_RISK"
        )
      ],
      dateFrom: "1970-01-01",
      dateTo: "now"
    };

    axios
      .all([
        getProfile(stakeholderCompanyId, profile),
        getInteractions(stakeholderCompanyId),
        getUsers("companies", stakeholderCompanyId, {
          stakeholderDepartmentParentName: true,
          stakeholderDepartmentId: true
        }),
        getDepartments(stakeholderCompanyId)
      ])
      .then(
        axios.spread((profileResp, interactionsResp, usersResp, dptResp) => {
          commit("set_interactions", interactionsResp.data.records);
          commit("set_dashboard", profileResp.data.records);
          commit("set_users", usersResp.data.records);
          commit("set_departments", dptResp.data.records);
          commit("set_dashboard_done");
        })
      );
  },
  reset({ commit }) {
    commit("reset");
  }
};

const mutations = {
  set_interactions(state, data) {
    state.interactions = data;
  },
  set_users(state, data) {
    state.users = data;
  },
  set_departments(state, data) {
    state.departments = data;
  },
  set_dashboard(state, data) {
    state.data = data;
    state.score = extractValueFromProfile(
      data,
      "DIMENSION_GROUP_SCORING",
      "SURVEY_KYMATIO_SCORE_GLOBAL",
      "dimensionValue",
      state.interactions,
      !store.getters["Auth/hasModule"](["risk"])
        ? "DIMENSION_ELEMENT_KYMATIO_SCORE"
        : "DIMENSION_ELEMENT_KYMATIO_SCORE_OLD",
      null,
      false
    );
    state.risk = extractValueFromProfile(
      data,
      "DIMENSION_GROUP_RISK",
      "SURVEY_KYMATIO_GRI_GLOBAL",
      "dimensionValue",
      state.interactions,
      "DIMENSION_ELEMENT_AGGREGATED_RISK",
      null,
      false
    );
    state.cybersecurity.average = extractValueFromProfile(
      data,
      "DIMENSION_GROUP_CIBERSECURITY",
      "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL",
      "dimensionValue",
      state.interactions,
      "DIMENSION_ELEMENT_CIBER_RESULTS",
      null,
      false
    );
    state.cybersecurity.data = [
      {
        value: extractValueFromProfile(
          data,
          "DIMENSION_GROUP_CIBERSECURITY",
          "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL",
          "dimensionValue",
          state.interactions,
          "DIMENSION_ELEMENT_DATAPROTECTION",
          null,
          false
        ),
        id: "DIMENSION_ELEMENT_DATAPROTECTION",
        name: "dataProtection"
      },
      {
        value: extractValueFromProfile(
          data,
          "DIMENSION_GROUP_CIBERSECURITY",
          "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL",
          "dimensionValue",
          state.interactions,
          "DIMENSION_ELEMENT_COMMUNICATIONS",
          null,
          false
        ),
        id: "DIMENSION_ELEMENT_COMMUNICATIONS",
        name: "communications"
      },
      {
        value: extractValueFromProfile(
          data,
          "DIMENSION_GROUP_CIBERSECURITY",
          "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL",
          "dimensionValue",
          state.interactions,
          "DIMENSION_ELEMENT_MALWARE",
          null,
          false
        ),
        id: "DIMENSION_ELEMENT_MALWARE",
        name: "malware"
      },
      {
        value: extractValueFromProfile(
          data,
          "DIMENSION_GROUP_CIBERSECURITY",
          "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL",
          "dimensionValue",
          state.interactions,
          "DIMENSION_ELEMENT_PASSWORDMANAGEMENT",
          null,
          false
        ),
        id: "DIMENSION_ELEMENT_PASSWORDMANAGEMENT",
        name: "passwordManagement"
      },
      {
        value: extractValueFromProfile(
          data,
          "DIMENSION_GROUP_CIBERSECURITY",
          "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL",
          "dimensionValue",
          state.interactions,
          "DIMENSION_ELEMENT_WORKPLACE",
          null,
          false
        ),
        id: "DIMENSION_ELEMENT_WORKPLACE",
        name: "workplace"
      },
      {
        value: extractValueFromProfile(
          data,
          "DIMENSION_GROUP_CIBERSECURITY",
          "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL",
          "dimensionValue",
          state.interactions,
          "DIMENSION_ELEMENT_COMPLIANCE",
          null,
          false
        ),
        id: "DIMENSION_ELEMENT_COMPLIANCE",
        name: "compliance"
      },
      {
        value: extractValueFromProfile(
          data,
          "DIMENSION_GROUP_CIBERSECURITY",
          "SURVEY_KYMATIO_CYBERSECURITY_GLOBAL",
          "dimensionValue",
          state.interactions,
          "DIMENSION_ELEMENT_SOCIALENGINEERING",
          null,
          false
        ),
        id: "DIMENSION_ELEMENT_SOCIALENGINEERING",
        name: "socialEngineering"
      }
    ];
    state.wellbeing.average = extractValueFromProfile(
      data,
      "DIMENSION_GROUP_WORKING",
      "SURVEY_KYMATIO_CLIMATE_GLOBAL",
      "dimensionValue",
      state.interactions,
      "DIMENSION_ELEMENT_CLIMATE_LEVEL",
      null,
      false
    );
    if (!store.getters["Auth/hasModule"](["risk"])) {
      state.progress = extractValueFromProfile(
        data,
        "DIMENSION_GROUP_SCORING",
        "SURVEY_KYMATIO_SCORE_GLOBAL",
        "dimensionValue",
        state.interactions,
        "DIMENSION_ELEMENT_KYMATIO_GLOBAL_NOTE",
        null,
        false
      );
    } else {
      state.progress =
        extractValueFromProfile(
          data,
          "DIMENSION_GROUP_EXTRAS",
          "SURVEY_KYMATIO_INSIDER_RED",
          "dimensionValue",
          state.interactions,
          "DIMENSION_ELEMENT_PERCENTAGE_INTERVIEW",
          null,
          true
        ) / 10 ||
        extractValueFromProfile(
          data,
          "DIMENSION_GROUP_EXTRAS",
          "SURVEY_KYMATIO_INSIDER_GREEN",
          "dimensionValue",
          state.interactions,
          "DIMENSION_ELEMENT_PERCENTAGE_INTERVIEW",
          null,
          true
        ) / 10;
    }
  },
  set_dashboard_done(state) {
    state.dashboardStatus = "ready";
  },
  reset(state) {
    state.interactions = [];
    state.cybersecurity = {};
    state.wellbeing = {};
    state.risk = {
      score: false,
      users: false,
      departments: false
    };
    state.progress = 0;
    state.users = [];
    state.departments = [];
    state.dashboardStatus = "";
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
